import { useEffect, useMemo } from 'react';

import { getSessionId } from 'state/amplitude/amplitude-package';
import { RunningExperiments, useExperiments } from 'state/experiments';

import {
  getAmplitudeSessionExposureDedupeKey,
  getExperimentAssignmentExposureKey,
} from './experiment-assignment-exposure-key';
import { orderPendingProcessModalExposureSessionLatch } from './order-pending-process-modal-exposure-sessions';

export const getOrderPendingProcessModalExperimentSessionDedupeKey = getAmplitudeSessionExposureDedupeKey;

export const clearOrderPendingProcessModalExperimentExposureSessionsForTests = () => {
  orderPendingProcessModalExposureSessionLatch.clear();
};

const useOrderPendingProcessModalExperimentAssignment = () => {
  const { getVariantValue } = useExperiments();

  return getVariantValue(RunningExperiments.OrderPendingProcessModal);
};

export const useOrderPendingProcessModalExperimentVariant = () =>
  useOrderPendingProcessModalExperimentAssignment()?.value;

/**
 * Confirmed treatment only. Use for actions that should only start for guests actually
 * bucketed into treatment, e.g. persisting a new pending-submitted-order record.
 * Defaults to control when the variant is unresolved so checkout remains safe if Amplitude is slow.
 */
export const useEnableOrderPendingProcessModalExperiment = () => {
  const variantValue = useOrderPendingProcessModalExperimentVariant();

  return variantValue === 'treatment';
};

/**
 * Treatment OR unresolved. Use for guarding/recovering an *existing* pending-submitted-order
 * (recovery UI, back-navigation guards, duplicate-submit prevention): once an order is already
 * in flight, losing that safety net to a slow or failed Amplitude fetch on a cold reload is worse
 * than running it for a guest who later turns out to be control. `isLoading` alone isn't enough:
 * ExperimentsProvider still flips it to false after a failed fetch, leaving the variant
 * `undefined` rather than a confirmed non-treatment value, so treat any unresolved variant
 * (loading or not) as unresolved and only disable once a concrete non-treatment value is known.
 */
export const useEnableOrderPendingProcessModalExperimentOrUnresolved = () => {
  const { isLoading } = useExperiments();
  const variantValue = useOrderPendingProcessModalExperimentVariant();

  return isLoading || variantValue === undefined || variantValue === 'treatment';
};

/**
 * Logs experiment exposure once per Amplitude session from checkout payment surfaces.
 */
export const useExposeOrderPendingProcessModalExperiment = (shouldExpose = true) => {
  const { logExposure } = useExperiments();
  const variant = useOrderPendingProcessModalExperimentAssignment();
  const variantValue = variant?.value;
  const amplitudeSessionId = getSessionId()?.toString();
  const dedupeKey = getOrderPendingProcessModalExperimentSessionDedupeKey(amplitudeSessionId);
  const assignmentExposureKey = useMemo(
    () =>
      dedupeKey && variant ? getExperimentAssignmentExposureKey({ dedupeKey, variant }) : undefined,
    [dedupeKey, variant]
  );

  useEffect(() => {
    if (
      !shouldExpose ||
      !variantValue ||
      !assignmentExposureKey ||
      orderPendingProcessModalExposureSessionLatch.hasHandled(assignmentExposureKey)
    ) {
      return;
    }

    if (logExposure(RunningExperiments.OrderPendingProcessModal, { dedupeKey })) {
      orderPendingProcessModalExposureSessionLatch.markHandled(assignmentExposureKey);
    }
  }, [assignmentExposureKey, dedupeKey, logExposure, shouldExpose, variantValue]);
};
